/**
 * Chat Area - Message list + input bar for active conversation
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Upload } from 'lucide-react';
import { useChatConversation, useSendChatMessage, useCreateConversation, useUploadReferenceImage } from '@/hooks/useChatImage';
import { ChatMessageBubble } from './ChatMessageBubble';
import { ChatInputBar } from './ChatInputBar';
import { ChatEmptyState } from './ChatEmptyState';
import { ChatLoadingIndicator } from './ChatLoadingIndicator';
import { SelectedImagesBar } from './SelectedImagesBar';

interface Props {
  conversationId: string | null;
  onConversationCreated: (id: string) => void;
}

const MAX_SELECTED_IMAGES = 4;

export function ChatArea({ conversationId, onConversationCreated }: Props) {
  const { data, isLoading } = useChatConversation(conversationId);
  const createMutation = useCreateConversation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [suggestedRatio, setSuggestedRatio] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const visibleMessages = (data?.messages ?? []).filter((msg) => msg.role !== 'upload');

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [visibleMessages.length, pendingText]);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- reset on conversation change
  useEffect(() => { setSelectedImages([]); }, [conversationId]);

  const toggleImage = useCallback((imageId: string, url: string, aspectRatio?: string) => {
    setSelectedImages((prev) => {
      const exists = prev.find((i) => i.id === imageId);
      if (exists) {
        const next = prev.filter((i) => i.id !== imageId);
        if (next.length === 0) setSuggestedRatio(null);
        return next;
      }
      if (prev.length >= MAX_SELECTED_IMAGES) return prev;
      if (aspectRatio) setSuggestedRatio(aspectRatio);
      return [...prev, { id: imageId, url }];
    });
  }, []);

  const removeImage = useCallback((imageId: string) => {
    setSelectedImages((prev) => prev.filter((i) => i.id !== imageId));
  }, []);

  const addUploadedImage = useCallback((imageId: string, url: string) => {
    setSelectedImages((prev) => {
      if (prev.length >= MAX_SELECTED_IMAGES) return prev;
      if (prev.find((i) => i.id === imageId)) return prev;
      return [...prev, { id: imageId, url }];
    });
  }, []);

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) setIsDragging(false);
  }, []);

  if (!conversationId) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <ChatEmptyState onNewConversation={handleNew} />
      </div>
    );
  }

  async function handleNew() {
    const conv = await createMutation.mutateAsync({});
    onConversationCreated(conv.id);
  }

  return (
    <div
      className="flex-1 flex flex-col min-w-0 relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={(e) => { e.preventDefault(); setIsDragging(false); }}
    >
      <ChatHeader title={data?.title} isLoading={isLoading} />
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {visibleMessages.map((msg) => (
          <ChatMessageBubble
            key={msg.id}
            message={msg}
            conversationId={conversationId}
            selectedImageIds={selectedImages.map((i) => i.id)}
            onToggleImage={toggleImage}
          />
        ))}
        {pendingText && <PendingUserMessage text={pendingText} />}
        {conversationId && <SendingIndicator conversationId={conversationId} />}
      </div>
      {selectedImages.length > 0 && (
        <SelectedImagesBar images={selectedImages} onRemove={removeImage} />
      )}
      <ActiveInputBar
        conversationId={conversationId}
        selectedImageIds={selectedImages.map((i) => i.id)}
        selectedCount={selectedImages.length}
        suggestedAspectRatio={suggestedRatio}
        onSending={setPendingText}
        onSent={() => { setSelectedImages([]); setPendingText(null); setSuggestedRatio(null); }}
        onImageUploaded={addUploadedImage}
      />
      {isDragging && conversationId && (
        <DropOverlay conversationId={conversationId} onDone={(id, url) => { setIsDragging(false); addUploadedImage(id, url); }} onCancel={() => setIsDragging(false)} />
      )}
    </div>
  );
}

export interface SelectedImage {
  id: string;
  url: string;
}

function ChatHeader({ title, isLoading }: { title?: string | null; isLoading: boolean }) {
  if (isLoading) return null;
  return title ? (
    <div className="px-4 py-2.5 border-b border-neutral-200 dark:border-neutral-800">
      <h2 className="text-sm font-medium truncate">{title}</h2>
    </div>
  ) : null;
}

function SendingIndicator({ conversationId }: { conversationId: string }) {
  const sendMutation = useSendChatMessage(conversationId);
  if (!sendMutation.isPending) return null;
  return <ChatLoadingIndicator />;
}

function PendingUserMessage({ text }: { text: string }) {
  return (
    <div className="flex gap-3 px-4 py-3 justify-end">
      <div className="max-w-[75%] order-first">
        <div className="rounded-2xl px-4 py-2.5 bg-neutral-800 dark:bg-neutral-700 text-white opacity-70">
          <p className="text-sm whitespace-pre-wrap">{text}</p>
        </div>
      </div>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-200 dark:bg-neutral-700">
        <User className="h-4 w-4 text-neutral-600 dark:text-neutral-300" />
      </div>
    </div>
  );
}

interface ActiveInputBarProps {
  conversationId: string;
  selectedImageIds: string[];
  selectedCount: number;
  suggestedAspectRatio: string | null;
  onSending: (text: string) => void;
  onSent: () => void;
  onImageUploaded: (imageId: string, url: string) => void;
}

function ActiveInputBar({ conversationId, selectedImageIds, selectedCount, suggestedAspectRatio, onSending, onSent, onImageUploaded }: ActiveInputBarProps) {
  const sendMutation = useSendChatMessage(conversationId);
  const uploadMutation = useUploadReferenceImage(conversationId);
  const { i18n } = useTranslation();

  const handleSend = (text: string, settings: { aspect_ratio?: string; resolution?: string }) => {
    onSending(text);
    sendMutation.mutate(
      { text, image_ids: selectedImageIds.length > 0 ? selectedImageIds : undefined, locale: i18n.language, ...settings },
      { onSuccess: onSent, onError: () => onSent() },
    );
  };

  const handleUpload = useCallback((files: File[]) => {
    for (const file of files) {
      uploadMutation.mutate(file, {
        onSuccess: (data) => {
          if (data.success) onImageUploaded(data.image_id, data.url);
        },
      });
    }
  }, [uploadMutation, onImageUploaded]);

  return (
    <ChatInputBar
      onSend={handleSend}
      onUpload={handleUpload}
      isLoading={sendMutation.isPending}
      isUploading={uploadMutation.isPending}
      disabled={false}
      canUpload={selectedCount < MAX_SELECTED_IMAGES}
      suggestedAspectRatio={suggestedAspectRatio}
    />
  );
}

interface DropOverlayProps {
  conversationId: string;
  onDone: (imageId: string, url: string) => void;
  onCancel: () => void;
}

function DropOverlay({ conversationId, onDone, onCancel }: DropOverlayProps) {
  const { t } = useTranslation();
  const uploadMutation = useUploadReferenceImage(conversationId);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) { onCancel(); return; }
    for (const file of files) {
      uploadMutation.mutate(file, {
        onSuccess: (data) => { if (data.success) onDone(data.image_id, data.url); },
        onError: () => onCancel(),
      });
    }
  }, [uploadMutation, onDone, onCancel]);

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-primary-500/10 border-2 border-dashed border-primary-500 rounded-lg backdrop-blur-sm"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      onDragLeave={(e) => { e.preventDefault(); onCancel(); }}
    >
      <div className="flex flex-col items-center gap-2 text-primary-600 dark:text-primary-400">
        <Upload className="h-8 w-8" />
        <span className="text-sm font-medium">{t('chatImage.dropHere')}</span>
      </div>
    </div>
  );
}
